import React, { useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { ChatMessage } from "@lifeat/shared/rooms/room-chat/RoomChatTypes";
import { calculateDaysBetweenDates, formatDateStandardTime, formatMonthDateString } from "@lifeat/shared/util/DateUtil";
import LifeAtTextBlockFormatted from "@common/LifeAtText/LifeAtTextBlockFormatted";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { FontSize } from "@themes/Fonts";
import { ScrollbarStyles } from "@themes/ScrollbarStyles";
import UserAvatar from "@user/components/UserAvatar/UserAvatar";
import RoomMemberDisplayName from "../../RoomMemberDisplayName";
import { RoomParams } from "../../RoomTypes";
import { PublicUserInfoForRoom } from "../RoomChatTypes";
import RoomModerationActionMenu from "./RoomModerationActionMenu";

const ChatMessageContainer = styled.div<{ transparent: boolean }>`
    width: 100%;
    display: flex;
    padding: 10px;
    position: relative;
    opacity: ${(props: { transparent: boolean }) => (props.transparent ? `0.7` : `1`)};
`;

const UserAviContainer = styled.div`
    height: 100%;
    width: fit-content;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 10px;
    overflow: hidden;
    width: 100%;
`;

const MessageHeader = styled.div`
    display: flex;
    align-items: baseline;
    gap: 5px;
`;

const MessageContent = styled.div`
    display: flex;
    font-size: ${FontSize.normal};
    color: ${(props) => props.theme.textColorPrimary};
    word-break: break-word;
    overflow-y: scroll;
    max-height: 150px;
    ${ScrollbarStyles};
`;

const MessageTimestamp = styled.span`
    font-size: ${FontSize.small};
    color: ${(props) => props.theme.textColorSecondary};
    white-space: nowrap;
`;

const SENDING_LABEL = "sending...";

interface MessageBubbleProps {
    readonly messageId: string;
    readonly authorInfo: PublicUserInfoForRoom | undefined;
    readonly message: ChatMessage["message"];
    readonly timeStamp: ChatMessage["sentTimestamp"];
    readonly isInteractingBlocked: boolean;
    readonly sending?: boolean;
}
export default function MessageBubble(props: MessageBubbleProps) {
    const { roomId }: RoomParams = useParams();
    const { user, userData }: UserContextType = useUserContext();

    const isHost = isRoomOwnedByUser(roomId, userData);
    const isMessageAuthor = props.authorInfo?.uid === user?.uid;

    const shouldShowActionMenu =
        props.authorInfo && !props.isInteractingBlocked && !props.sending && (!isMessageAuthor || isHost);

    const [isActionMenuVisible, setIsActionMenuVisible] = useState(false);

    return (
        <ChatMessageContainer
            transparent={props.sending ?? false}
            onMouseEnter={() => setIsActionMenuVisible(true)}
            onMouseLeave={() => setIsActionMenuVisible(false)}
        >
            <UserAviContainer>
                <UserAvatar
                    size={"small"}
                    userDisplayName={props.authorInfo?.userDisplayName ?? "User"}
                    userAviUrl={props.authorInfo?.userAviUrl ?? ""}
                />
            </UserAviContainer>

            <Content>
                <MessageHeader>
                    <RoomMemberDisplayName
                        displayName={props.authorInfo?.userDisplayName}
                        isDisplayNameBold={true}
                        isHost={!!props.authorInfo?.isHost}
                    />
                    <MessageTimestamp>
                        {props.sending ? SENDING_LABEL : formatMessageTimestamp(props.timeStamp)}
                    </MessageTimestamp>
                </MessageHeader>

                <MessageContent>
                    <LifeAtTextBlockFormatted text={props.message} highlightLinks={true} />
                </MessageContent>
            </Content>
            {shouldShowActionMenu && (
                <RoomModerationActionMenu
                    roomId={roomId}
                    targetUid={props.authorInfo.uid}
                    isHost={isHost}
                    messageId={props.messageId}
                    isVisible={isActionMenuVisible}
                />
            )}
        </ChatMessageContainer>
    );
}

/**
 * Formats date and time string
 * Ex: (current day) "2:28pm", (any previous day) "Feb 2, 12:47am"
 * @param timestamp date in UNIX timestamp
 */
function formatMessageTimestamp(timestamp: number) {
    const today = new Date();
    const messageDate = new Date(timestamp);

    let formattedTime: string;

    const numDaysBetween = calculateDaysBetweenDates(messageDate, today);
    if (numDaysBetween < 1) {
        formattedTime = formatDateStandardTime(messageDate);
    } else {
        formattedTime = `${formatMonthDateString(messageDate)}, ${formatDateStandardTime(messageDate)}`;
    }

    return formattedTime;
}
